/**
 * Custom values for the generated custom theme layer that are also available for an
 * extended instance of tailwind-merge.
 */

export const fontSize = {
	'xs': 'var(--body-xs)',
	'sm': 'var(--body-sm)',
	'md': 'var(--body-md)',
	'lg': 'var(--body-lg)',

	'highlight-xs': 'var(--font-size-highlight-xs)',
	'highlight-sm': 'var(--font-size-highlight-sm)',
	'highlight-md': 'var(--font-size-highlight-md)',
	'highlight-lg': 'var(--font-size-highlight-lg)',
	'highlight-xl': 'var(--font-size-highlight-xl)',

	// Responsive font sizes
	'h1': 'var(--font-size-h1)',
	'h2': 'var(--font-size-h2)',
	'h3': 'var(--font-size-h3)',
	'h4': 'var(--font-size-h4)',
	'h5': 'var(--font-size-h5)',
	'h6': 'var(--font-size-h6)',

	// Just 'inherit' collides with text color, which also uses 'text-' prefix.
	'inherit-size': 'inherit',
};

export const lineHeight = {
	'heading-2xs': '1.2',
	'heading-xs': '1.2',
	'heading-sm': '1.167',
	'heading-md': '1.142',
	'heading-lg': '1.112',
	'heading-xl': '1.2',
	'heading-2xl': '1.167',
	'body-xs': '1.3333',
	'body-sm': '1.428',
	'body-md': '1.5',
	'body-lg': '1.556',
	'none': '1',
	// To generally tighten specific areas.
	'tight': '1.25',
};

export const maxWidth = {
	pageStandard: '102.75rem', // 1644px
	pageNarrow: '80rem', // 1280px
	pageExtraNarrow: '50.625rem', // 810px
	// Generate classes for `max-w-col-X` where X is 1 to 12 and each class
	// matches a column width in the grid when it's at its largest size.
	...Object.fromEntries(
		Array.from({ length: 12 }).map((_, i) => {
			const col = i + 1;
			const colSize = 115;
			const gapSize = 24;
			const width = colSize * col + gapSize * (col - 1);
			return [`col-${col}`, `${width / 16}rem`];
		}),
	),
};

export const borderRadius = {
	'2xs': '0.125rem', // 2px
	'xs': '0.25rem', // 4px
	'sm': '0.375rem', // 6px
	'md': '0.5rem', // 8px
	'lg': '1rem', // 16px
	'xl': '1.5rem', // 24px
	'full': '9999px',
	'none': '0',
};

const outlineThickness = 2;
const outlineOffset = 3;
export const spacing = {
	'focus-outline-thickness': `${outlineThickness}px`,
	'focus-outline-offset': `${outlineOffset}px`,
	'focus-outline-size': `${outlineThickness + outlineOffset}px`,

	// Responsive spacing
	'r-xs': 'var(--r-space-xs)',
	'r-sm': 'var(--r-space-sm)',
	'r-md': 'var(--r-space-md)',
	'r-lg': 'var(--r-space-lg)',
	'r-xl': 'var(--r-space-xl)',
	'r-2xl': 'var(--r-space-2xl)',
	'r-3xl': 'var(--r-space-3xl)',
	'r-4xl': 'var(--r-space-4xl)',
};

export const boxShadow = {
	centerBottom:
		'0 0.5rem 1.5rem 0 --alpha(var(--background-color-shadow) / 20%)',
	center: '0 0 1.5rem 0 --alpha(var(--background-color-shadow) / 20%)',
	none: 'none',
};

export const zIndex = {
	// FAQ search
	faqSearchOverlay: '55',
	faqSearchResults: '56',
	faqSearchField: '57',

	// Chat button
	chatButton: '70',

	// Header
	pageHeaderPageOverlay: '85',
	pageHeaderPart: '86',
	searchResults: '87',
	searchField: '88',
	belowStickyPageHeader: '88',
	stickyPageHeader: '89',
	toggledMenu: '90',

	popover: '300',
	popoverLayer2: '301',
	popoverLayer3: '302',

	pageLoadingSpinner: '700',

	toast: '900',

	navigationProgressBar: '999',
};

/** Get the keys of a custom theme config object. */
export function getCustomThemeKeys(group: Record<string, unknown>) {
	const keys = Object.keys(group);
	return keys.filter((key) => key !== 'DEFAULT');
}
